import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  Typography,
  useTheme,
  Fade,
  styled,
  MobileStepper,
  IconButton,
  useMediaQuery
} from '@mui/material';
import AccountForm from './AccountForm';
import BusinessForm from './BusinessForm';
import ServicesForm from './ServicesForm';
import TeamSizeForm from './TeamSizeForm';
import AddressForm from './AddressForm';
import PackageForm from './PackageForm';
import SuccessStep from './SuccessStep';
import { PartnerFormData } from '../../types/partner';
import { db } from '../../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import emailService from '../../utils/emailService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { registerPartner } from '../../utils/partnerRegistrationHandler';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const steps = [
  'Tài khoản',
  'Doanh nghiệp',
  'Dịch vụ',
  'Quy mô',
  'Địa chỉ',
  'Gói dịch vụ',
  'Hoàn tất'
];

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.07)',
  transition: 'all 0.3s ease-in-out',
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  }
}));

const StyledStep = styled(Step)(({ theme }) => ({
  '& .MuiStepLabel-root': {
    transition: 'all 0.3s ease',
  },
  '& .MuiStepLabel-completed': {
    color: theme.palette.success.main,
  },
  '& .MuiStepLabel-active': {
    transform: 'scale(1.05)',
  }
}));

const StepHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1, 0),
}));

const PartnerRegistration: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<PartnerFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    website: '',
    services: [],
    teamSize: '1-5',
    address: '',
    hasNoFixedAddress: false,
    latitude: 0,
    longitude: 0,
    selectedPackage: 'free',
    createdAt: new Date(),
    status: 'pending'
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    setIsLoading(true);
    
    try {
      console.log("Handling partner registration with formData:", formData);
      
      // Sử dụng handler mới để đăng ký đối tác
      const result = await registerPartner(formData);
      
      if (result.success) {
        setActiveStep(steps.length - 1);
      } else {
        toast.error("Có lỗi xảy ra trong quá trình đăng ký. Vui lòng thử lại sau.");
      }
    } catch (error) {
      console.error("Error during registration:", error);
      toast.error("Có lỗi xảy ra trong quá trình đăng ký. Vui lòng thử lại sau.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepSubmit = (stepData: Partial<PartnerFormData>) => {
    setFormData((prev) => ({ ...prev, ...stepData }));
    
    if (activeStep === steps.length - 2) {
      handleSubmit();
    } else {
      handleNext();
    }
  };

  const renderStepTitle = () => {
    switch (activeStep) {
      case 0: return 'Tạo tài khoản';
      case 1: return 'Thông tin doanh nghiệp';
      case 2: return 'Dịch vụ cung cấp';
      case 3: return 'Quy mô đội ngũ';
      case 4: return 'Địa chỉ';
      case 5: return 'Chọn gói dịch vụ';
      case 6: return 'Đăng ký thành công';
      default: return '';
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Fade in={true} timeout={300}>
            <Box>
              <AccountForm
                onSubmit={handleStepSubmit}
                initialValues={formData}
              />
            </Box>
          </Fade>
        );
      case 1:
        return (
          <Fade in={true} timeout={300}>
            <Box>
              <BusinessForm
                onSubmit={handleStepSubmit}
                onBack={handleBack}
                initialValues={formData}
              />
            </Box>
          </Fade>
        );
      case 2:
        return (
          <Fade in={true} timeout={300}>
            <Box>
              <ServicesForm
                onSubmit={handleStepSubmit}
                onBack={handleBack}
                initialValues={formData}
              />
            </Box>
          </Fade>
        );
      case 3:
        return (
          <Fade in={true} timeout={300}>
            <Box>
              <TeamSizeForm
                onSubmit={handleStepSubmit}
                onBack={handleBack}
                initialValues={formData}
              />
            </Box>
          </Fade>
        );
      case 4:
        return (
          <Fade in={true} timeout={300}>
            <Box>
              <AddressForm
                onSubmit={handleStepSubmit}
                onBack={handleBack}
                initialValues={formData}
              />
            </Box>
          </Fade>
        );
      case 5:
        return (
          <Fade in={true} timeout={300}>
            <Box>
              <PackageForm
                onSubmit={handleStepSubmit}
                onBack={handleBack}
                initialValues={formData}
              />
            </Box>
          </Fade>
        );
      case 6:
        return (
          <Fade in={true} timeout={300}>
            <Box>
              <SuccessStep
                isLoading={isLoading}
                onFinish={() => {
                  // Redirect to home or partner dashboard
                }}
              />
            </Box>
          </Fade>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
      <StyledPaper>
        {isMobile && activeStep > 0 && activeStep < steps.length - 1 && (
          <StepHeader>
            <IconButton
              size="small"
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={600}>
              {renderStepTitle()}
            </Typography>
          </StepHeader>
        )}

        {!isMobile && (
          <>
            <Typography variant="h4" align="center" gutterBottom sx={{ 
              fontWeight: 600, 
              color: theme.palette.primary.main,
              mb: 3
            }}>
              Đăng ký đối tác
            </Typography>

            <Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{ 
                mb: 5,
                '& .MuiStepConnector-line': {
                  minHeight: 3,
                  borderTopWidth: 3,
                  borderRadius: 3,
                  transition: 'all 0.3s ease'
                },
                '& .MuiStepConnector-active': {
                  '& .MuiStepConnector-line': {
                    borderColor: theme.palette.primary.main,
                  }
                },
                '& .MuiStepConnector-completed': {
                  '& .MuiStepConnector-line': {
                    borderColor: theme.palette.success.main,
                  }
                }
              }}
            >
              {steps.map((label, index) => (
                <StyledStep key={label}>
                  <StepLabel>{label}</StepLabel>
                </StyledStep>
              ))}
            </Stepper>
          </>
        )}

        {isMobile && activeStep !== steps.length - 1 && (
          <MobileStepper
            variant="progress"
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            sx={{ 
              background: 'transparent', 
              padding: 0,
              marginBottom: 3,
              '& .MuiLinearProgress-root': {
                height: 6,
                borderRadius: 3
              }
            }}
            nextButton={<Box />}
            backButton={<Box />}
          />
        )}

        {renderStep()}
      </StyledPaper>
    </Container>
  );
};

export default PartnerRegistration; 